// @givmo/fundraisers-module — a campaign's "Suggested goal for fundraiser pages" (EMC-WEB6; Jonathan,
// 2026-11-01 00:09): an optional setting beside "Who can create a fundraiser page?", the amount the
// fundraiser page wizard's Goal step starts with for anyone who creates a page under the campaign (a
// Board Ask's directors included; the ask has no field of its own). Blank, the wizard's field starts
// empty beside "No goal".
//
// The settings read serves it while the email campaigns release is on (`suggested_page_goal`, dark ==
// missing), so the control shows only where the key is served. It is ahead of the snapshot's regen, so
// it is read off the config by hand and written as a typed extension of the settings body
// (agent_docs/api-client.md, "Additive request fields ahead of a regen").

import { useEffect, useState } from 'react';
import type { P2pConfigOut, P2pConfigRequest } from '@givmo/api-client';
import { Button, TextField } from '@givmo/design-system';
import { GOAL_MESSAGE, canonicalGoal, cleanGoal, displayGoal, isGoalish } from './fundraiser-form';

export const SUGGESTED_GOAL_COPY = {
  label: 'Suggested goal for fundraiser pages (USD, optional)',
  line: 'It fills in the goal for anyone who creates a fundraiser page under this campaign. They can change it, or choose No goal.',
  save: 'Save',
  none: 'None',
} as const;

/** The settings body with the key the snapshot doesn't carry yet. */
export type SuggestedGoalBody = P2pConfigRequest & { suggested_page_goal?: string | null };

/** The stored suggestion, `null` for none, `undefined` where the key isn't served (the release is off). */
export function suggestedPageGoal(config: P2pConfigOut): string | null | undefined {
  const record = config as unknown as Record<string, unknown>;
  if (!('suggested_page_goal' in record)) return undefined;
  const value = record.suggested_page_goal;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function SuggestedPageGoal({
  config,
  canManage,
  pending,
  onChange,
}: {
  config: P2pConfigOut;
  canManage: boolean;
  pending: boolean;
  onChange: (body: SuggestedGoalBody) => void;
}) {
  const stored = suggestedPageGoal(config);
  const shown = displayGoal(stored ?? null);
  const [value, setValue] = useState(shown);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setValue(shown), [shown]);
  if (stored === undefined) return null;
  if (!canManage)
    return (
      <div className="text-s14" data-testid="p2p-suggested-goal-read">
        <p className="text-text-secondary">{SUGGESTED_GOAL_COPY.label}</p>
        <p>{shown ? `$${shown}` : SUGGESTED_GOAL_COPY.none}</p>
      </div>
    );
  const changed = canonicalGoal(cleanGoal(value)) !== canonicalGoal(stored ?? '');
  const save = () => {
    if (!isGoalish(value)) return setError(GOAL_MESSAGE);
    setError(null);
    const cleaned = cleanGoal(value);
    const body: SuggestedGoalBody = { suggested_page_goal: cleaned.length > 0 ? cleaned : null };
    onChange(body);
  };
  return (
    <div className="flex flex-col gap-8" data-testid="p2p-suggested-goal">
      <div className="flex flex-col gap-12 sm:flex-row sm:items-end">
        <TextField
          label={SUGGESTED_GOAL_COPY.label}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          inputMode="decimal"
          disabled={pending}
          error={error !== null}
          errorMessage={error ?? undefined}
          className="sm:w-[320px]"
          testId="p2p-suggested-goal-input"
        />
        <Button
          appearance="saas"
          tone="secondary"
          disabled={!changed || pending}
          loading={pending}
          onClick={save}
          testId="p2p-suggested-goal-save"
        >
          {SUGGESTED_GOAL_COPY.save}
        </Button>
      </div>
      <p className="text-s13 leading-h15 text-text-secondary">{SUGGESTED_GOAL_COPY.line}</p>
    </div>
  );
}
